import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { PaymentPlanModel, PaymentPlanSearchParams } from '@frontend/api-payment-plans';
import { PrintDialog } from '@frontend/assets';
import { useTranslation } from '@frontend/i18n';
import { useNavigate, useSearch } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { ContentLoader, PrintIcon, SortingValue, Table, UpdateIcon } from '@frontend/design-system';
import { usePaymentPlanSearch, useQueryAllPaymentPlans } from '../../../hooks/use-payment-plan-search';
import { usePrintDialogue } from '../../../hooks/use-print-dialogue';
import { usePaymentPlanShallowStore } from '../../../providers/payment-plans-provider';
import { PaymentsTableInstances } from '../../../utils/helpers';
import { PrintHeader } from '../../PrintHeader';
import { PaymentPlanFilter } from '../PaymentPlanFilter';
import { PaymentPlanSummaryCard } from '../PaymentPlanSummary';
import { generateColumns } from './generate-columns';

const FIELD_TO_ORDER: { [key: string]: PaymentPlanSearchParams['order'] } = {
  startedAt: 'startedAt',
  person: 'person',
  balanceRemaining: 'balanceRemaining',
  paymentAmount: 'paymentAmount',
  nextPaymentAt: 'nextPaymentAt',
  planName: 'name',
};

const getOrder = <T,>(res: SortingValue[], defaultSort: T): T => {
  if (res.length) {
    const { id, value } = res[0] ?? {};
    const order = !!id && FIELD_TO_ORDER[id];
    if (id && order) return `${value === 'asc' ? '' : '-'}${order}` as T;
  }
  return `-${defaultSort}`.replace(/--/, '-') as T;
};

export const PaymentPlanList = () => {
  const { t } = useTranslation('payments');
  const navigate = useNavigate();
  const search = useSearch({ from: '/payments/payment-plans', shouldThrow: false });
  const shouldUpdateList = !!search?.updateList;
  const columns = useMemo(() => generateColumns, []);

  const { numRows, setNumRows, setOrder, filter, setFilter, setSelectedPaymentPlan } = usePaymentPlanShallowStore(
    'numRows',
    'setNumRows',
    'setOrder',
    'filter',
    'setFilter',
    'setSelectedPaymentPlan'
  );
  const { paymentPlans, loading, hasMore, hasPrevious, fetchNextPage, fetchPreviousPage, currentPage, refetch } =
    usePaymentPlanSearch();

  const { showPrint, handlePrint, setShowPrint } = usePrintDialogue({ timeoutDelay: 2000 });

  useEffect(() => {
    if (shouldUpdateList) {
      refetch();
    }
  }, [shouldUpdateList]);

  return (
    <>
      <Table
        colConfig={columns}
        data={paymentPlans}
        isLoading={loading}
        loadingRowsCount={numRows}
        globalTrackingId='pay-portal-paymentplans'
        tableInstanceId={PaymentsTableInstances.PaymentPlans}
        manualSortBy
        hasResponsiveColWidths
        hasResizeColumns
        manualFilters
        isPaginated
        emptyStateConfig={{
          type: 'payments',
          header: t('No data to display'),
        }}
        styleConfig={{
          columns: [
            {
              id: '*',
              cellStyler: css`
                padding: ${theme.spacing(1, 1.5)};
              `,
              headerStyler: css`
                padding: ${theme.spacing(2, 1.5)};
              `,
            },
          ],
        }}
        onSortChange={(res) => {
          setOrder(getOrder<PaymentPlanSearchParams['order']>(res, 'startedAt'));
        }}
        rowActions={{
          onRowClick: (paymentPlan: PaymentPlanModel) => {
            setSelectedPaymentPlan(paymentPlan);
            navigate({ to: '/payments/payment-plans/$planId', params: { planId: paymentPlan.id } });
          },
        }}
        tableActions={[
          { label: t('Refresh'), Icon: UpdateIcon, onClick: () => refetch() },
          { label: t('Print'), Icon: PrintIcon, disabled: !paymentPlans.length, onClick: () => handlePrint() },
        ]}
        manualFiltersRender={(modalProps, setShowNotification) => (
          <PaymentPlanFilter
            initialFilter={filter}
            onApply={setFilter}
            modalProps={modalProps}
            setShowNotification={setShowNotification}
          />
        )}
        manualPaginationConfig={{
          page: currentPage,
          hasNext: hasMore && !loading,
          hasPrevious: hasPrevious && currentPage !== 1 && !loading,
          onNumRowsChange: (num) => setNumRows(num),
          defaultRowsPerPage: numRows,
          handleChange: (prevOrNext: 'prev' | 'next') => {
            if (prevOrNext === 'prev') fetchPreviousPage();
            if (prevOrNext === 'next') fetchNextPage();
          },
        }}
      />

      {showPrint && <PrintFriendly onClose={() => setShowPrint(false)} />}
    </>
  );
};

const PrintFriendly = ({ onClose }: { onClose: () => void }) => {
  const { allPaymentPlans, summary, allPaymentPlansLoading } = useQueryAllPaymentPlans();

  return allPaymentPlansLoading ? (
    <ContentLoader show={allPaymentPlansLoading} />
  ) : !allPaymentPlans?.length ? null : (
    <PrintDialog show={true} onClose={onClose} landscape>
      <div
        css={css`
          margin: ${theme.spacing(2)};
        `}
      >
        <PrintHeader />
        <PaymentPlanSummaryCard
          summary={summary}
          loading={false}
          css={css`
            margin-bottom: ${theme.spacing(3)};
          `}
        />
        <Table
          wrapperStyle={css`
            height: fit-content;
            width: 100%;
          `}
          colConfig={generateColumns}
          data={allPaymentPlans}
          tableInstanceId={PaymentsTableInstances.PaymentPlans}
        />
      </div>
    </PrintDialog>
  );
};
